import { removeLocalStorage } from '@/utils/common/storage'
import { goPage } from '@/utils/browser/page'

import { getI18nTranslate } from '@/utils/i18nUtils'
import { useMessage } from '@/composables/useMessage'
import { STORAGE_USER } from '@/constants/storageKey.const'
import { useUserStore } from '@/stores/user.stores'
import { CanceledError } from 'axios'

const { openMessage } = useMessage()

/**
 * 根據 HTTP 狀態碼顯示對應的錯誤訊息（console.error）。
 *
 * 目前只處理已知的 HTTP 錯誤碼（400、404、500、502），其餘碼一律輸出 `Unknown error code`。
 * 訊息文字來自 i18n，key 格式為 `Common.Response.*`。
 *
 * @param errorCode - HTTP 狀態碼
 * @param errorMessage - 後端回傳的原始錯誤訊息（附加於 console 輸出，供除錯用）
 */
export const errorCodeHandler = (errorCode: number, errorMessage: string): void => {
  const errorMessages: { [key: number]: string } = {
    400: 'Common.Response.BadRequest',
    403: 'Common.Response.Forbidden',
    404: 'Common.Response.NotFound',
    500: 'Common.Response.InternalError',
    502: 'Common.Response.ServerError'
  }

  const errorMessageKey = errorMessages[errorCode]

  if (errorMessageKey) {
    return console.error(getI18nTranslate(errorMessageKey), errorMessage)
  }

  return console.error('Unknown error code', errorMessage)
}

/**
 * 處理 HTTP 401 未授權錯誤。
 *
 * 收到 401 時，表示 token 已過期或無效，需要：
 * 1. 清除本地登入狀態（`initLoginState`）
 * 2. 移除 localStorage 中的 user 資料
 * 3. 顯示錯誤提示後導向登入頁
 *
 * 非 401 狀態碼不做任何處理（回傳 `void`），由呼叫方決定後續行為。
 *
 * @param errorCode - HTTP 狀態碼
 * @returns 401 時回傳 rejected Promise；其他碼回傳 `void`
 */
export const unauthorizedHandler = (errorCode: number): Promise<Error> | void => {
  if (errorCode !== 401) return

  const { initLoginState } = useUserStore()

  initLoginState()
  removeLocalStorage(STORAGE_USER, true)
  openMessage('error', getI18nTranslate('Common.Response.Unauthorized'), {}, () => {
    goPage('/login')
  })

  return Promise.reject(new Error(getI18nTranslate('Common.Response.Unauthorized')))
}

/**
 * Composable / Service 層的通用 catch 處理。
 *
 * 將各種類型的錯誤統一轉為字串後 `throw`，方便上層統一捕捉。
 * 若為主動取消的請求（`CanceledError`），則靜默忽略，不 throw。
 *
 * @param error - catch 捕捉到的未知錯誤
 * @throws 非取消錯誤時，拋出包含詳細訊息的 `Error`
 */
export const catchErrorHandler = (error: unknown) => {
  let errorMessage: string

  if (error instanceof Error) {
    errorMessage = error.message
  } else {
    try {
      errorMessage = JSON.stringify(error)
    } catch {
      errorMessage = String(error) // 如果 JSON.stringify 失败，使用 String 处理
    }
  }

  if (error instanceof CanceledError) {
    console.warn('Request canceled:', errorMessage)
    return // 如果是請求被取消，直接返回
  }

  // 抛出新的 Error，帶有詳細的錯誤信息，方便開發者查看
  throw new Error(errorMessage)
}
